/**
 * Browser-side billing configuration.
 *
 * The plan catalog, tier names and entitlement policy live in
 * `supabase/functions/_shared/paddleMapping.ts` so the UI and the webhook can
 * never disagree about what a plan is or what it grants. This module only adds
 * the browser-specific pieces: reading `VITE_`-prefixed environment values and
 * formatting prices for display.
 *
 * Only the Paddle **client-side token** and **price IDs** appear here. Both are
 * designed to be public. The Paddle API key and the webhook secret are
 * server-only and must never be referenced from this file.
 */
import {
  PLANS,
  PLAN_LIST,
  TIER_LABELS,
  buildPriceIdMap,
  priceIdForPlan,
  type BillingInterval,
  type PlanDefinition,
  type PlanKey,
  type Tier,
} from "../../supabase/functions/_shared/paddleMapping";

export {
  PLANS,
  PLAN_LIST,
  TIER_LABELS,
  TIERS,
  ADVISOR_TIERS,
  PAST_DUE_GRACE_DAYS,
  CANCELLATION_GRACE_DAYS,
  decideEntitlement,
} from "../../supabase/functions/_shared/paddleMapping";
export type {
  BillingInterval,
  PlanDefinition,
  PlanKey,
  Tier,
  SubscriptionStatus,
  EntitlementDecision,
} from "../../supabase/functions/_shared/paddleMapping";

/** Vite injects these at build time; all are non-secret. */
const ENV = import.meta.env as unknown as Record<string, string | undefined>;

/**
 * Sandbox vs live. This build is sandbox-only; `live` requires an explicit
 * opt-in via environment and a separate go-live review.
 */
export const PADDLE_ENVIRONMENT: "sandbox" | "production" =
  ENV.VITE_PADDLE_ENV === "production" ? "production" : "sandbox";

/** Paddle client-side token. Public by design, but still not hardcoded. */
export const PADDLE_CLIENT_TOKEN = ENV.VITE_PADDLE_CLIENT_TOKEN?.trim() ?? "";

/** True once the operator has supplied a client token. */
export const isPaddleConfigured = (): boolean => PADDLE_CLIENT_TOKEN.length > 0;

/** Paddle price ID for an internal plan, or null when not yet configured. */
export function getPriceId(planKey: PlanKey): string | null {
  return priceIdForPlan(planKey, ENV, "VITE_");
}

/** Price ID -> plan lookup, for reading back a checkout result in the UI. */
export const browserPriceIdMap = () => buildPriceIdMap(ENV, "VITE_");

/** Plans whose Paddle price ID is configured and which can actually be bought. */
export function purchasablePlans(opts: { includeCohortPlans?: boolean } = {}): PlanDefinition[] {
  return PLAN_LIST.filter(
    (p) => (opts.includeCohortPlans || !p.cohortRestricted) && getPriceId(p.key) !== null,
  );
}

/** Plans for one tier, configured or not — used to render an accurate price card. */
export function plansForTier(tier: Tier): PlanDefinition[] {
  return PLAN_LIST.filter((p) => p.tier === tier);
}

/** e.g. 7900 -> "$79". Whole dollars render without cents. */
export function formatPlanPrice(plan: PlanDefinition): string {
  const dollars = plan.amountMinor / 100;
  const body = Number.isInteger(dollars)
    ? `$${dollars.toLocaleString("en-US")}`
    : `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${body} USD`;
}

export function intervalLabel(interval: BillingInterval): string {
  return interval === "year" ? "per year" : "per month";
}

/** Short marketing description per tier. Copy only — no entitlement meaning. */
export const TIER_DESCRIPTIONS: Record<Tier, string> = {
  family:
    "For one family running their own banking system. Unlimited scenarios and commitment agreements, full schedules and reports.",
  advisor_core:
    "For advisors guiding client families. Everything in Family, plus linked client access and read-only client review.",
  advisor_pro:
    "For advisor teams and practices. Everything in Advisor Core, with a higher client capacity and priority support.",
};

/** Ordered tiers as shown on the pricing page. */
export const TIER_ORDER: readonly Tier[] = ["family", "advisor_core", "advisor_pro"];

export function tierLabel(tier: Tier): string {
  return TIER_LABELS[tier];
}
